import {useLocation} from "react-router-dom";
import React, {useEffect, useMemo, useState} from "react";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import Box from "@mui/material/Box";
import {Tooltip, Typography} from "@mui/material";
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner,
} from "material-react-table";
import MenuItem from "@mui/material/MenuItem";
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import {SETTINGS_TYPES} from "../../../data/SettingsType";
import UtilityServiceOrganizationPictures from "../../../API/common/UtilityServiceOrganizationPictures";
import {BigImage, NewsSettingsModal} from "./Picture";
import ButtonCreate from "../../../components/UI/button/ButtonCreate";
import OrganizationMediaService from "../../../API/common/OrganizationMediaService";
import {TEXTURE_PICTURE_SYSTEM_TYPE} from "../../../data/OrganizationCompositionPictureSystemType";
import {clearTexturePictureCache} from "../../../utils/compositionTexturePictureCache";
import {withTextureCatalogFallback} from "../../../utils/mockCompositionTextureCatalog";

const parseTexturedPicturesResponse = (payload) => {
    if (!Array.isArray(payload)) {
        return [];
    }
    return payload.filter((row) => row && typeof row === "object");
};

const sortTextureRows = (rows) =>
    [...rows].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

/** Поля таблицы из PictureDto (POST /organization/media/pictures). */
const toTextureTableRow = (dto) => {
    if (!dto || dto.id == null) {
        return null;
    }
    return {
        id: dto.id,
        name: dto.name,
        relativeUrlSmall: dto.relativeUrlSmall,
        relativeUrl: dto.relativeUrl,
        priority: dto.priority,
        pictureSystemType: dto.pictureSystemType,
    };
};

const CompositionTextures = ({
    itemId,
    handleModalAlert,
    isModal,
    selectedStringId,
    setSelectedString,
}) => {
    const pathname = useLocation().pathname;
    const [allRows, setAllRows] = useState([]);
    const [usedTextureFallback, setUsedTextureFallback] = useState(false);
    const [isFirst, setIsFirst] = useState(true);
    const [bigImage, setBigImage] = useState(null);
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: isModal ? 10 : 100,
        totalElements: 0,
        searchData: "",
    });

    const tableOptionsHandle = (nextState) => {
        setTableOptions(nextState);
        if (nextState.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(`${pathname}+${itemId}`, "pageSize", nextState.pageSize);
        }
    };

    const getStartPageSize = () => {
        const pagesData = JSON.parse(localStorage.getItem("pagesData")) || {};
        const pageSize = pagesData[`${pathname}+${itemId}`]?.pageSize;
        const searchQuery = pagesData[`${pathname}+${itemId}`]?.searchQuery;
        return {searchQuery, pageSize};
    };

    useEffect(() => {
        if (isFirst) {
            const {searchQuery, pageSize} = getStartPageSize();
            setTableOptions((prev) => ({
                ...prev,
                searchData: searchQuery || prev.searchData,
                pageSize: pageSize || prev.pageSize,
            }));
            setIsFirst(false);
            return;
        }

        UtilityServiceOrganizationPictures.getAllTextured()
            .then((response) => {
                const {rows, usedFallback} = withTextureCatalogFallback(
                    parseTexturedPicturesResponse(response?.data),
                );
                setAllRows(sortTextureRows(rows));
                setUsedTextureFallback(usedFallback);
            })
            .catch((error) => {
                const {rows, usedFallback} = withTextureCatalogFallback([]);
                setAllRows(sortTextureRows(rows));
                setUsedTextureFallback(usedFallback);
                handleModalAlert(error);
            });
    }, [isFirst]);

    const filteredRows = useMemo(() => {
        const query = (tableOptions.searchData || "").trim().toLowerCase();
        if (!query) {
            return allRows;
        }
        return allRows.filter((row) => (row.name || "").toLowerCase().includes(query));
    }, [allRows, tableOptions.searchData]);

    const data = useMemo(() => {
        const start = tableOptions.pageIndex * tableOptions.pageSize;
        return filteredRows.slice(start, start + tableOptions.pageSize);
    }, [filteredRows, tableOptions.pageIndex, tableOptions.pageSize]);

    useEffect(() => {
        setTableOptions((prev) => ({
            ...prev,
            totalElements: filteredRows.length,
        }));
    }, [filteredRows.length]);

    const handleClose = () => {
        setModal(false);
    };

    const handleSubmit = (values, file, closeModal) => {
        setLoading(true);
        OrganizationMediaService.addTexturePicture(values?.name, file)
            .then((response) => {
                const row = toTextureTableRow(response?.data);
                if (row) {
                    setAllRows((prev) => {
                        const without = prev.filter((item) => item.id !== row.id);
                        return sortTextureRows([...without, row]);
                    });
                }
                clearTexturePictureCache("organization");
                closeModal();
                handleModalAlert("Данные добавлены", "success");
            })
            .catch((error) => {
                handleModalAlert(error);
            })
            .finally(() => setLoading(false));
    };

    const handleDelete = (row) => {
        if (row?.mock) {
            setAllRows((prev) => prev.filter((item) => item.id !== row.id));
            handleModalAlert("Тестовая текстура убрана из списка", "success");
            return;
        }
        UtilityServiceOrganizationPictures.delete(row.id)
            .then(() => {
                clearTexturePictureCache("organization");
                setAllRows((prev) => prev.filter((item) => item.id !== row.id));
                handleModalAlert("Данные удалены", "success");
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    };

    const columns = useMemo(
        () => [
            {
                header: "Наименование",
                accessorKey: "name",
                size: 360,
                minSize: 280,
                maxSize: 420,
                grow: false,
                Cell: ({row, cell}) => (
                    <Tooltip arrow placement="bottom" title={row.original.name}>
                        <Box
                            sx={{
                                display: "block",
                                maxWidth: "340px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {cell.getValue()}
                        </Box>
                    </Tooltip>
                ),
            },
            {
                header: "Текстура",
                accessorKey: "relativeUrlSmall",
                size: 780,
                minSize: 400,
                grow: true,
                Cell: ({cell, row}) => {
                    const value = cell.getValue() || row.original.relativeUrl;
                    return (
                        <Box
                            sx={{
                                margin: "4px",
                                height: "120px",
                                width: "min(100%, 720px)",
                                minWidth: "280px",
                                borderRadius: "8px",
                                backgroundImage: value ? `url(${value})` : "none",
                                backgroundColor: value ? "transparent" : "#EAEBEA",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "cover",
                                cursor: value ? "pointer" : "default",
                            }}
                            onClick={() => value && setBigImage(row.original.relativeUrl || value)}
                        />
                    );
                },
            },
        ],
        [],
    );

    const menuItem = [{type: "delete", text: "Удалить", handler: handleDelete}];

    return (
        <Box
            sx={{
                overflow: "auto",
                maxWidth: "1200px",
            }}
        >
            {!isModal && (
                <Box sx={{display: "flex", flexDirection: "column", gap: "8px", paddingBottom: "30px"}}>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <Typography sx={{fontSize: "24px", fontWeight: 700, lineHeight: 1}}>
                            {SETTINGS_TYPES[itemId]}
                        </Typography>
                        <ButtonCreate text={"Добавить"} width={130} onClick={() => setModal(true)}/>
                    </Box>
                    {usedTextureFallback && (
                        <Typography sx={{fontSize: "14px", color: "text.secondary"}}>
                            by-textured пуст или недоступен — показан тестовый пул с фронта (не сохраняется на бэк).
                        </Typography>
                    )}
                </Box>
            )}
            <MaterialReactTable
                renderTopToolbar={({table}) => (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "7px",
                            backgroundColor: (theme) =>
                                table.getSelectedRowModel().rows.length > 0
                                    ? theme.palette.primary.light
                                    : "transparent",
                        }}
                    >
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: "flex"}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                )}
                muiTableContainerProps={{
                    sx: {
                        width: "1200px",
                        maxHeight: `calc(100vh - 420px)`,
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        p: "0px 0px 0px 10px",
                        m: "0px 0px 0px 0px",
                        border: "none",
                        "& td:after": {
                            "&:hover": {
                                backgroundColor: "red",
                            },
                        },
                        "&:not(:last-of-type)": {
                            borderRight: "1px solid #EAEBEA",
                        },
                        "&:first-of-type": !isModal && {
                            padding: 0,
                            width: "50px",
                            height: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: (theme) => theme.palette.text.light,
                            "& .MuiButtonBase-root": {
                                borderRadius: "4px",
                                width: "24px",
                                height: "24px",
                                opacity: 1,
                                margin: 0,
                                padding: 0,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.stroke,
                                },
                            },
                        },
                        "& li": {
                            listStyleType: "none",
                            "& a": {
                                color: "#006CCC",
                                textDecoration: "none",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            },
                        },
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        border: "1px solid #EAEBEA",
                        backgroundColor: "#F5F6FA",
                        padding: "10px",
                        paddingBottom: " 0.2rem",
                        paddingTop: "0.2rem",
                    },
                }}
                muiTableBodyRowProps={({row}) => ({
                    onClick: () => setSelectedString && setSelectedString(row.original),
                    sx: () => ({
                        height: "36px",
                        cursor: setSelectedString && "pointer",
                        "&:hover& td:after": {
                            display: "none",
                        },
                        "&:nth-of-type(even)": {
                            backgroundColor:
                                row.original.id === selectedStringId
                                    ? (theme) => theme.palette.primary.light
                                    : (theme) => theme.palette.primary.lightGrey,
                            "&.MuiTableRow-root.Mui-selected": {
                                backgroundColor: (theme) => theme.palette.primary.light,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            "&:hover": {
                                backgroundColor:
                                    row.original.id === selectedStringId
                                        ? (theme) => theme.palette.primary.light50
                                        : (theme) => theme.palette.primary.stroke,
                            },
                        },
                        backgroundColor:
                            row.original.id === selectedStringId
                                ? (theme) => theme.palette.primary.light
                                : "white",
                        "&:hover": {
                            backgroundColor:
                                row.original.id === selectedStringId
                                    ? (theme) => theme.palette.primary.light50
                                    : (theme) => theme.palette.primary.stroke,
                        },
                        "&.MuiTableRow-root.Mui-selected": {
                            backgroundColor: (theme) => theme.palette.primary.light70,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.light50,
                            },
                        },
                    }),
                })}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        header: "",
                        size: 3,
                        minSize: 3,
                        grow: false,
                    },
                }}
                muiSearchTextFieldProps={{
                    placeholder: `Поиск`,
                    sx: {
                        paddingTop: "5px",
                        paddingBottom: "0px",
                        "& .MuiCollapse-root": {
                            borderRadius: "12px",
                            height: 40,
                            width: 290,
                            "& .MuiSvgIcon-root": {
                                color: "#737478",
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: 0,
                            },
                        },
                    },
                    variant: "outlined",
                }}
                columns={columns}
                data={data}
                onGlobalFilterChange={(value) =>
                    tableOptionsHandle({...tableOptions, searchData: value, pageIndex: 0})
                }
                onPaginationChange={(updater) => {
                    tableOptionsHandle(updater(tableOptions));
                }}
                manualPagination={true}
                state={{showGlobalFilter: true, pagination: tableOptions}}
                rowCount={tableOptions.totalElements}
                selectionCss={{
                    backgroundColor: "lightblue",
                    borderWidth: "2px",
                    borderStyle: "solid",
                    borderColor: "blue",
                    borderRadius: "4px",
                }}
                renderRowActionMenuItems={({closeMenu, row}) =>
                    menuItem.map((el) => (
                        <MenuItem
                            key={el.type}
                            onClick={() => {
                                el.handler(row.original);
                                closeMenu();
                            }}
                            sx={{
                                margin: "0 8px",
                                borderRadius: "4px",
                                "&:hover": {backgroundColor: (theme) => theme.palette.primary.light},
                            }}
                        >
                            {el.text}
                        </MenuItem>
                    ))
                }
                localization={MRT_Localization_RU}
                enableRowActions={!isModal}
                enableToolbarInternalActions={false}
            />
            <NewsSettingsModal
                title={"Добавить"}
                open={modal}
                onClose={handleClose}
                handleSubmit={handleSubmit}
                editingPictureSystemType={false}
                fixedPictureSystemType={TEXTURE_PICTURE_SYSTEM_TYPE}
                initValue={{name: ""}}
                loading={loading}
            />
            <BigImage bigImage={bigImage} setBigImage={setBigImage}/>
        </Box>
    );
};

export default CompositionTextures;
